import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  BulkUpdateJob,
  BulkUpdateProvider,
  BulkUpdateResult,
  BulkUpdateStatus,
  UpdateData,
} from 'models';
import { Observable, map } from 'rxjs';

import {
  S9ApiBulkUpdateJobDefinition,
  S9ApiBulkUpdateStatusDefinition,
  createBulkUpdateJobFromApi,
  createBulkUpdateResultFromApi,
  createBulkUpdateStatusFromApi,
} from '../models/bulk-update';

import { Square9ApiConfig } from './square9-api-config.model';
import { SQUARE9_API_CONFIG } from './square9-api-config.token';

/** Bulk Update Service. */
@Injectable({
  providedIn: 'root',
})
export class Square9ApiBulkUpdateService implements BulkUpdateProvider {
  private basePath: string;
  constructor(
    private http: HttpClient,
    @Inject(SQUARE9_API_CONFIG) private config: Square9ApiConfig
  ) {
    this.config.apiUrl$.subscribe((apiUrl) => {
      this.basePath = apiUrl;
    });
  }
  /** @inheritdoc */
  cancelJob(jobId: string): Observable<void> {
    return this.http.put<any>(
      `${this.basePath}/bulkupdate/cancel`,
      {},
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        params: new HttpParams().set('jobId', jobId),
      }
    );
  }
  /** @inheritdoc */
  deleteJob(jobId: string): Observable<void> {
    return this.http.delete<any>(`${this.basePath}/bulkupdate`, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      params: new HttpParams().set('jobId', jobId),
    });
  }
  /** @inheritdoc */
  getJobResult(jobId: string): Observable<BulkUpdateResult> {
    return this.http
      .get<any>(`${this.basePath}/bulkupdate/result`, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        params: new HttpParams().set('jobId', jobId),
      })
      .pipe(map((apiJob) => createBulkUpdateResultFromApi(apiJob)));
  }
  /** @inheritdoc */
  getJobStatus(jobId: string): Observable<BulkUpdateStatus> {
    return this.http
      .get<S9ApiBulkUpdateStatusDefinition>(
        `${this.basePath}/bulkupdate/status`,
        {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          params: new HttpParams().set('jobId', jobId),
        }
      )
      .pipe(map((apiJob) => createBulkUpdateStatusFromApi(apiJob)));
  }
  /** @inheritdoc */
  getJobs(): Observable<BulkUpdateJob[]> {
    return this.http
      .get<S9ApiBulkUpdateJobDefinition[]>(`${this.basePath}/bulkupdate`, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(map((jobs) => jobs.map(createBulkUpdateJobFromApi)));
  }
  /** @inheritdoc */
  initializeJob(
    databaseId: number,
    searchId: number,
    updateData: UpdateData
  ): Observable<BulkUpdateJob> {
    if (databaseId <= 0 || searchId <= 0) {
      throw new Error('Database ID and Search ID must be positive integers');
    }

    return this.http
      .post<S9ApiBulkUpdateJobDefinition>(
        `${this.basePath}/bulkupdate`,
        {
          criteria: updateData.criteria,
          updates: updateData.updates,
          mvUpdates: updateData.mvUpdates,
          overwriteSystemFields: updateData.overwriteSystemFields,
        },
        {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          params: new HttpParams()
            .set('databaseId', databaseId)
            .set('searchId', searchId),
        }
      )
      .pipe(map((apiJob) => createBulkUpdateJobFromApi(apiJob)));
  }
  /** @inheritdoc */
  startUpdate(jobId: string): Observable<void> {
    return this.http.put<void>(
      `${this.basePath}/bulkupdate/start`,
      {},
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        params: new HttpParams().set('jobId', jobId),
      }
    );
  }
}
